import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supportedLanguages } from '@/i18n';
import { useTranslation } from 'react-i18next';

const DOMAIN = 'https://www.greenin.org';
const OG_IMAGE_DEFAULT = 'https://storage.googleapis.com/gpt-engineer-file-uploads/fGAjohtGSHQajGzfSLC8HVdVcQl1/social-images/social-1772744911619-GreenIn_Americas_2026.webp';

const ROUTE_OG_IMAGE: Record<string, string> = {
  '/programs/safe-sustainable-communities': `${DOMAIN}/og/og-safe-communities.jpg`,
  '/programs/green-talent-digital-future': `${DOMAIN}/og/og-green-talent.jpg`,
  '/programs/purpose-driven-smes': `${DOMAIN}/og/og-purpose-smes.jpg`,
  '/programs/ai-for-good-responsible-ai': `${DOMAIN}/og/og-ai-for-good.jpg`,
  '/programs/circular-impact-greenin-shop': `${DOMAIN}/og/og-circular-impact.jpg`,
};

/**
 * Maps route paths (without locale prefix) to i18n OG key prefixes.
 */
const ROUTE_OG_MAP: Record<string, string> = {
  '': 'og.home',
  '/programs/safe-sustainable-communities': 'og.programs.safeCommunities',
  '/programs/green-talent-digital-future': 'og.programs.greenTalent',
  '/programs/purpose-driven-smes': 'og.programs.purposeSMEs',
  '/programs/ai-for-good-responsible-ai': 'og.programs.aiForGood',
  '/programs/circular-impact-greenin-shop': 'og.programs.circularShop',
  '/library': 'og.library',
  '/public': 'og.public',
  '/sdgs': 'og.sdgs',
  '/transparency/results': 'og.results',
  '/privacy': 'og.privacy',
  '/terms': 'og.terms',
  '/cookies': 'og.cookies',
  '/accessibility': 'og.accessibility',
};

const LOCALE_TO_OG: Record<string, string> = {
  en: 'en_US',
  es: 'es_LA',
  pt: 'pt_BR',
  fr: 'fr_FR',
};

function setMeta(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property';
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (el) {
    el.content = content;
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    el.content = content;
    document.head.appendChild(el);
  }
}

const HreflangTags = () => {
  const location = useLocation();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    const pathMatch = location.pathname.match(/^\/(en|es|pt|fr)(\/.*)?$/);
    const pathWithoutLocale = pathMatch ? (pathMatch[2] || '') : '';
    const lang = i18n.language;
    const currentUrl = `${DOMAIN}/${lang}${pathWithoutLocale}`;

    // --- Hreflang tags ---
    document.querySelectorAll('link[data-i18n]').forEach(el => el.remove());

    supportedLanguages.forEach((l) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = l;
      link.href = `${DOMAIN}/${l}${pathWithoutLocale}`;
      link.setAttribute('data-i18n', 'true');
      document.head.appendChild(link);
    });

    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = `${DOMAIN}/en${pathWithoutLocale}`;
    xDefault.setAttribute('data-i18n', 'true');
    document.head.appendChild(xDefault);

    // --- Canonical ---
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonical) {
      canonical.href = currentUrl;
    }

    // --- HTML lang ---
    document.documentElement.lang = lang;

    // --- OG / Twitter meta tags ---
    const ogKey = ROUTE_OG_MAP[pathWithoutLocale] ?? 'og.home';
    const ogTitle = t(`${ogKey}.title`);
    const ogDesc = t(`${ogKey}.description`);

    // Page title
    document.title = ogTitle;

    // Open Graph
    setMeta('og:title', ogTitle);
    setMeta('og:description', ogDesc);
    setMeta('og:url', currentUrl);
    setMeta('og:type', 'website');
    setMeta('og:site_name', 'GreenIn Impact');
    const ogImage = ROUTE_OG_IMAGE[pathWithoutLocale] || OG_IMAGE_DEFAULT;
    setMeta('og:image', ogImage);
    setMeta('og:locale', LOCALE_TO_OG[lang] || 'en_US');

    // Add og:locale:alternate for other languages
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    supportedLanguages.filter(l => l !== lang).forEach(l => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale:alternate');
      meta.content = LOCALE_TO_OG[l] || l;
      document.head.appendChild(meta);
    });

    // Twitter
    setMeta('twitter:card', 'summary_large_image', true);
    setMeta('twitter:title', ogTitle, true);
    setMeta('twitter:description', ogDesc, true);
    setMeta('twitter:image', ogImage, true);

    // Standard meta description
    setMeta('description', ogDesc, true);

    return () => {
      document.querySelectorAll('link[data-i18n]').forEach(el => el.remove());
      document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    };
  }, [location.pathname, i18n.language, t]);

  return null;
};

export default HreflangTags;
